import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

const sqlEditorSx = {
  '& .MuiInputBase-root': {
    fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
    fontSize: '0.85rem',
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
  },
  '& .MuiInputBase-input': {
    fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
    fontSize: '0.85rem',
  },
};

export interface OptionQueryFormData {
  id?: string;
  queryKey: string;
  description: string;
  sql: string;
}

interface CreateEditOptionQueryModalProps {
  open: boolean;
  isEdit: boolean;
  onClose: () => void;
  onSave: (data: OptionQueryFormData) => void;
  saving: boolean;
  saveError: string | null;
}

const CreateEditOptionQueryModal: React.FC<CreateEditOptionQueryModalProps> = ({
  open,
  isEdit,
  onClose,
  onSave,
  saving,
  saveError,
}) => {
  const { selectedQuery, loading } = useAppSelector((state) => state.optionQueries);

  const [formData, setFormData] = useState({
    queryKey: '',
    description: '',
    sql: '',
  });

  useEffect(() => {
    if (isEdit && selectedQuery) {
      setFormData({
        queryKey: selectedQuery.queryKey || '',
        description: selectedQuery.description || '',
        sql: selectedQuery.sql || '',
      });
    } else if (!isEdit) {
      setFormData({
        queryKey: '',
        description: '',
        sql: '',
      });
    }
  }, [selectedQuery, isEdit, open]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const data: OptionQueryFormData = {
      queryKey: formData.queryKey,
      description: formData.description,
      sql: formData.sql,
    };

    if (isEdit && selectedQuery) {
      data.id = selectedQuery.id;
    }

    onSave(data);
  };

  const isFormValid = formData.queryKey && formData.description && formData.sql;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Editar Query de Opciones' : 'Crear Query de Opciones'}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {saveError && (
              <Alert severity="error">{saveError}</Alert>
            )}

            <TextField
              label="Query Key"
              value={formData.queryKey}
              onChange={(e) => handleChange('queryKey', e.target.value)}
              fullWidth
              required
              disabled={saving}
            />

            <TextField
              label="Descripción"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              required
              multiline
              rows={2}
              disabled={saving}
            />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                SQL *
              </Typography>
              <TextField
                value={formData.sql}
                onChange={(e) => handleChange('sql', e.target.value)}
                fullWidth
                required
                multiline
                minRows={6}
                maxRows={16}
                disabled={saving}
                placeholder="SELECT id, name FROM schema.table ORDER BY name ASC;"
                sx={sqlEditorSx}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !isFormValid}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditOptionQueryModal;
